import { resolve } from "path";
import type { WakeAppResult } from "@gencow/core";
import { resolveRealtimeConnectionLimit } from "./server-realtime-connection-limit.js";
import { consumeGatewayRealtimeTicket } from "./ws-gateway.js";
import { recordNodeRouteMetric } from "./server-node-routing-metrics.js";
import { serveStaticFile } from "./server-platform-static-file.js";
import { meterResponseBandwidth } from "./response-bandwidth-meter.js";
import {
  createCustomDomainResolver,
  normalizePlatformProxyHost,
} from "./server-platform-app-proxy-domain.js";
import {
  isSignedNodeProxyRequestPath,
  maybeProxyRemoteServerAppRequest,
  maybeUpgradeRemoteServerAppWebSocket,
  type ServerPlatformNodeRouteRecord,
} from "./server-platform-node-routing.js";

export { shouldUseStaticIndexFallbackForMissingPath } from "./static-file-routing";
export {
  createCustomDomainResolver,
  normalizePlatformProxyHost,
  type CustomDomainResolution,
} from "./server-platform-app-proxy-domain.js";
export {
  parseRealtimeConnectionLimit,
  resolveRealtimeConnectionLimit,
} from "./server-realtime-connection-limit.js";

type LoggerLike = Pick<Console, "log" | "warn" | "error">;
type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;
const INTERNAL_DOMAIN_VERIFY_PATH = "/internal/domain/verify";
const HOSTED_ANALYTICS_SCRIPT_PATH = "/_gencow/analytics.js";

type RuntimeUnavailableResult = {
  error: string;
  code: string;
  status: string;
  hint?: string;
  retryAfterSec?: number;
  httpStatus: number;
};

type AppRow = {
  id: number;
  name: string;
  port: number;
  status: string;
  dataDir: string;
  currentDeployId?: unknown;
  staticDeployedAt?: unknown;
  hasBackend: boolean;
  plan?: string | null;
  customDomain?: string | null;
  customDomainStatus?: string | null;
  nodeId?: string | null;
};

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function buildHostedAnalyticsScriptTag(appRow: AppRow): string {
  const deploy = appRow.currentDeployId === null || appRow.currentDeployId === undefined
    ? ""
    : String(appRow.currentDeployId);
  const deployAttr = deploy ? ` data-deploy="${escapeHtmlAttribute(deploy)}"` : "";
  return `<script defer src="${HOSTED_ANALYTICS_SCRIPT_PATH}" data-site="app:${escapeHtmlAttribute(String(appRow.id))}"${deployAttr}></script>`;
}

type PlatformAppProxyDeps = {
  app: {
    use(path: string, handler: (context: any, next: () => Promise<unknown>) => Promise<unknown>): void;
  };
  platformDomain: string;
  subdomainRegex: RegExp;
  getBunServer(): any;
  findActiveDomainApp(host: string): Promise<string | null>;
  findAppRow(appName: string): Promise<AppRow | null>;
  currentNodeId?: string | null;
  loadAppRouteNode?(nodeId: string): Promise<ServerPlatformNodeRouteRecord | null>;
  proxyRemoteNodeRequest?(params: {
    c: any;
    host: string;
    appName: string;
    nodeId: string;
    origin: string;
    body?: BodyInit;
  }): Promise<Response>;
  proxyRemoteNodeWebSocket?(params: {
    c: any;
    host: string;
    appName: string;
    nodeId: string;
    origin: string;
  }): Promise<Response>;
  generateInternalToken(appId: number, appName: string): string;
  markAppAsCrashed(appName: string): Promise<void>;
  markHasBackend(appName: string): Promise<void>;
  rawSql: RawSql;
  analyticsRawSql?: RawSql;
  functionsPath: string;
  addProxyMetric(appName: string, bytes: number): void;
  createProxyRequestReplayPlan(request: Request): Promise<{
    initialBody: BodyInit | undefined;
    replayBody: BodyInit | undefined;
    canReplayAfterFailure: boolean;
  }>;
  buildAppRuntimeUnavailableResult(appName: string, status: string): RuntimeUnavailableResult;
  ensureAppRuntimeReady(
    appRow: AppRow,
    runtimeDeps: {
      getPort(appName: string): number | undefined;
      setPort(appName: string, port: number): void;
      removePortIfEquals(appName: string, expectedPort: number): boolean;
      resolveRuntimePort(appName: string): Promise<number | undefined>;
      hasRuntimeProcess(appName: string): Promise<boolean>;
      reapRuntimeExits?(appName: string): Promise<{ memoryExceeded?: boolean } | void>;
      probePort(appName: string, port: number): Promise<boolean>;
      wakeApp(appName: string): Promise<WakeAppResult>;
      provisionApp(
        appName: string,
        port: number,
        dataDir: string,
        appDbId: number,
        isProd: boolean,
        reason: string,
      ): Promise<void>;
      setAppMeta(appName: string, meta: { port: number; dataDir: string; appDbId: number }): void;
      markAccess(appName: string): void;
      logger: LoggerLike;
    },
    options: { stalePort: number | null },
  ): Promise<{ kind: "ready"; port: number } | ({ kind: "unavailable" } & RuntimeUnavailableResult)>;
  getLivePort(appName: string): number | undefined;
  setAppPort(appName: string, port: number): void;
  removeAppPort(appName: string): void;
  removeAppPortIfEquals(appName: string, expectedPort: number): boolean;
  wakeApp(appName: string): Promise<WakeAppResult>;
  provisionApp(
    appName: string,
    port: number,
    dataDir: string,
    appDbId: number,
    isProd: boolean,
    reason: string,
  ): Promise<void>;
  setAppMeta(appName: string, meta: { port: number; dataDir: string; appDbId: number }): void;
  markLastAccess(appName: string): void;
  hasLiveRuntimeProcess(appName: string): Promise<boolean>;
  resolveLiveRuntimePort(appName: string): Promise<number | undefined>;
  reapRuntimeExits?(appName: string): Promise<{ memoryExceeded?: boolean } | void>;
  logger?: LoggerLike;
};

export function buildRuntimeUnavailableBody(result: {
  error: string;
  code: string;
  status: string;
  hint?: string;
}) {
  return {
    error: result.error,
    code: result.code,
    status: result.status,
    ...(result.hint ? { hint: result.hint } : {}),
  };
}

export function buildRecoveredRetryBody() {
  return {
    error: "앱 런타임을 복구했습니다. 요청을 다시 시도해주세요.",
    code: "APP_RECOVERED_RETRY",
    hint: "대용량 또는 스트리밍 요청은 자동 재시도하지 않습니다.",
  };
}

export function isPublicAppBackendRoute(pathname: string): boolean {
  return /^\/(api|platform|internal|ws)\b/.test(pathname);
}

export function isDeniedPublicAppSystemPath(pathname: string): boolean {
  return /^\/(_admin|_dashboard)\b/.test(pathname);
}

export function buildTransientProxyFailureBody() {
  return {
    error: "앱 런타임 응답이 일시적으로 실패했습니다. 요청을 다시 시도해주세요.",
    code: "APP_PROXY_TRANSIENT_FAILURE",
    hint: "런타임 프로세스는 살아 있으므로 플랫폼이 앱을 crashed 상태로 전환하지 않았습니다.",
  };
}

export function buildUnknownCustomDomainBody(host: string) {
  return {
    error: `Domain ${host} is not registered or active`,
    code: "CUSTOM_DOMAIN_NOT_FOUND",
  };
}

function isPlatformProxyBypassHost(host: string, platformDomain: string): boolean {
  return (
    !host ||
    host === platformDomain ||
    host === `www.${platformDomain}` ||
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host === "0:0:0:0:0:0:0:1" ||
    /^127(?:\.\d{1,3}){3}$/.test(host)
  );
}

async function probeAppPort(port: number): Promise<boolean> {
  try {
    const probeRes = await fetch(`http://127.0.0.1:${port}/health/ready`, {
      method: "GET",
      signal: AbortSignal.timeout(2000),
    });
    return probeRes.ok;
  } catch {
    return false;
  }
}

function isLocalPeerAddress(address: string): boolean {
  return (
    address === "127.0.0.1" ||
    address === "::1" ||
    address === "0:0:0:0:0:0:0:1" ||
    address.startsWith("::ffff:127.")
  );
}

export function resolveForwardedClientIp(headers: Headers, peerAddress?: string | null): string | null {
  const peer = peerAddress?.trim() || "";
  if (peer && !isLocalPeerAddress(peer)) return peer;

  const realIp = headers.get("x-real-ip")?.trim();
  if (realIp) return realIp;

  const forwardedChain = (headers.get("x-forwarded-for") || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  return forwardedChain.at(-1) || peer || null;
}

export function normalizeProxyForwardedHeaders(headers: Headers, clientIp: string | null): void {
  headers.delete("cf-connecting-ip");
  headers.delete("x-real-ip");
  headers.delete("x-forwarded-for");
  if (!clientIp) return;
  headers.set("X-Real-IP", clientIp);
  headers.set("X-Forwarded-For", clientIp);
}

async function proxyAppRequest(params: {
  c: any;
  host: string;
  appName: string;
  port: number;
  url: URL;
  requestBody: BodyInit | undefined;
  addProxyMetric(appName: string, bytes: number): void;
}): Promise<Response> {
  const targetUrl = `http://127.0.0.1:${params.port}${params.url.pathname}${params.url.search}`;
  const headers = new Headers(params.c.req.raw.headers);
  const peerAddress =
    params.c.env?.ip && typeof params.c.env.ip.address === "string" ? params.c.env.ip.address : null;
  normalizeProxyForwardedHeaders(headers, resolveForwardedClientIp(headers, peerAddress));
  headers.set("X-Forwarded-Host", params.host);
  headers.set("X-Forwarded-Proto", "https");

  const proxyRes = await fetch(targetUrl, {
    method: params.c.req.method,
    headers,
    body: params.requestBody,
    // OAuth authorization redirects must be returned to the browser, not followed
    // by the platform proxy. based on this RFC 6749 section 4.1.2:
    // https://www.rfc-editor.org/rfc/rfc6749#section-4.1.2
    redirect: "manual",
    ...(params.requestBody !== undefined ? ({ duplex: "half" } as Record<string, string>) : {}),
  });

  return meterResponseBandwidth(
    new Response(proxyRes.body, {
      status: proxyRes.status,
      headers: proxyRes.headers,
    }),
    (bytes) => params.addProxyMetric(params.appName, bytes),
  );
}

type ProxyAppRequestImpl = typeof proxyAppRequest;

async function maybeServePlatformStorage(params: {
  pathname: string;
  appName: string;
  c: any;
  functionsPath: string;
  rawSql: RawSql;
}) {
  if (!shouldServePlatformStorageDirect(process.env)) {
    return null;
  }
  if (!/^\/api\/storage\/[0-9a-f-]+/.test(params.pathname)) {
    return null;
  }

  const { servePlatformStorage } = await import(
    resolve(params.functionsPath, "../src/serve-platform-storage.ts")
  );
  return await servePlatformStorage(params.appName, params.c, {
    rawQuery: params.rawSql,
  });
}

async function maybeServeHostedAnalytics(params: {
  pathname: string;
  appRow: AppRow;
  host: string;
  c: any;
  functionsPath: string;
  rawSql: RawSql;
  analyticsRawSql?: RawSql;
  logger: LoggerLike;
}) {
  if (!params.pathname.startsWith("/_gencow/analytics")) return null;
  const analytics = await import(resolve(params.functionsPath, "analytics-hosted.ts"));
  return await analytics.serveHostedAnalyticsRequest({
    c: params.c,
    app: params.appRow,
    host: params.host,
    rawSql: params.rawSql,
    analyticsRawSql: params.analyticsRawSql ?? params.rawSql,
    logger: params.logger,
  });
}

export function shouldServePlatformStorageDirect(env: NodeJS.ProcessEnv = process.env): boolean {
  return env.GENCOW_PLATFORM_STORAGE_DIRECT_SERVE === "1";
}

export async function proxyToBackendWithRecovery(params: {
  c: any;
  host: string;
  url: URL;
  appRow: AppRow;
  logger: LoggerLike;
  createProxyRequestReplayPlan(request: Request): Promise<{
    initialBody: BodyInit | undefined;
    replayBody: BodyInit | undefined;
    canReplayAfterFailure: boolean;
  }>;
  buildAppRuntimeUnavailableResult(appName: string, status: string): RuntimeUnavailableResult;
  ensureAppRuntimeReady: PlatformAppProxyDeps["ensureAppRuntimeReady"];
  getLivePort(appName: string): number | undefined;
  setAppPort(appName: string, port: number): void;
  removeAppPort(appName: string): void;
  removeAppPortIfEquals(appName: string, expectedPort: number): boolean;
  wakeApp(appName: string): Promise<WakeAppResult>;
  provisionApp: PlatformAppProxyDeps["provisionApp"];
  setAppMeta(appName: string, meta: { port: number; dataDir: string; appDbId: number }): void;
  markLastAccess(appName: string): void;
  hasLiveRuntimeProcess(appName: string): Promise<boolean>;
  resolveLiveRuntimePort(appName: string): Promise<number | undefined>;
  reapRuntimeExits?(appName: string): Promise<{ memoryExceeded?: boolean } | void>;
  markAppAsCrashed(appName: string): Promise<void>;
  addProxyMetric(appName: string, bytes: number): void;
  proxyAppRequestImpl?: ProxyAppRequestImpl;
}): Promise<Response> {
  if (params.appRow.status === "memory_exceeded") {
    const unavailable = params.buildAppRuntimeUnavailableResult(params.appRow.name, params.appRow.status);
    return params.c.json(buildRuntimeUnavailableBody(unavailable), unavailable.httpStatus);
  }

  const replayPlan = await params.createProxyRequestReplayPlan(params.c.req.raw);
  const mappedPort = params.getLivePort(params.appRow.name);
  const proxyRequest = params.proxyAppRequestImpl ?? proxyAppRequest;
  let attemptedFastPath = false;

  if (mappedPort) {
    attemptedFastPath = true;
    try {
      return await proxyRequest({
        c: params.c,
        host: params.host,
        appName: params.appRow.name,
        port: mappedPort,
        url: params.url,
        requestBody: replayPlan.initialBody,
        addProxyMetric: params.addProxyMetric,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      params.logger.warn(
        `[platform] ${params.appRow.name}: fast-path proxy failed on :${mappedPort}: ${message}`,
      );
    }
  }

  let runtime;
  try {
    runtime = await params.ensureAppRuntimeReady(
      params.appRow,
      {
        getPort: params.getLivePort,
        setPort: params.setAppPort,
        removePortIfEquals: params.removeAppPortIfEquals,
        resolveRuntimePort: params.resolveLiveRuntimePort,
        hasRuntimeProcess: params.hasLiveRuntimeProcess,
        reapRuntimeExits: params.reapRuntimeExits,
        probePort: async (_appName: string, port: number) => probeAppPort(port),
        wakeApp: params.wakeApp,
        provisionApp: params.provisionApp,
        setAppMeta: params.setAppMeta,
        markAccess: params.markLastAccess,
        logger: params.logger,
      },
      { stalePort: mappedPort ?? null },
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    params.logger.error(`[platform] ${params.appRow.name}: runtime recovery failed: ${message}`);
    params.removeAppPort(params.appRow.name);
    await params.markAppAsCrashed(params.appRow.name).catch((markError) => {
      params.logger.warn(
        `[platform] ${params.appRow.name}: failed to mark crashed after recovery error: ${markError}`,
      );
    });
    const unavailable = params.buildAppRuntimeUnavailableResult(params.appRow.name, "crashed");
    return params.c.json(buildRuntimeUnavailableBody(unavailable), unavailable.httpStatus);
  }

  if (runtime.kind === "unavailable") {
    if (runtime.retryAfterSec) {
      params.c.header("Retry-After", String(runtime.retryAfterSec));
    }
    return params.c.json(buildRuntimeUnavailableBody(runtime), runtime.httpStatus);
  }

  if (attemptedFastPath && !replayPlan.canReplayAfterFailure) {
    params.logger.warn(
      `[platform] ${params.appRow.name}: runtime recovered but request body is not replayable`,
    );
    return params.c.json(buildRecoveredRetryBody(), 503);
  }

  const retryBody = attemptedFastPath ? replayPlan.replayBody : replayPlan.initialBody;
  try {
    return await proxyRequest({
      c: params.c,
      host: params.host,
      appName: params.appRow.name,
      port: runtime.port,
      url: params.url,
      requestBody: retryBody,
      addProxyMetric: params.addProxyMetric,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    params.logger.error(
      `[platform] ${params.appRow.name}: retry proxy failed on :${runtime.port}: ${message}`,
    );
    return params.c.json(buildTransientProxyFailureBody(), 502);
  }
}

export function registerPlatformAppProxy(params: PlatformAppProxyDeps): void {
  const logger = params.logger ?? console;
  const platformDomainHost = normalizePlatformProxyHost(params.platformDomain);
  const { resolveCustomDomain, invalidateDomainCache } = createCustomDomainResolver({
    findActiveDomainApp: params.findActiveDomainApp,
  });
  (globalThis as any).__invalidateDomainCache = invalidateDomainCache;

  params.app.use("*", async (c, next) => {
    const requestPath = c.req.path ?? new URL(c.req.url).pathname;
    if (requestPath === INTERNAL_DOMAIN_VERIFY_PATH) {
      return next();
    }
    if (isSignedNodeProxyRequestPath(requestPath, c.req.raw.headers)) {
      return next();
    }

    const host = normalizePlatformProxyHost(c.req.header("host"));
    if (isPlatformProxyBypassHost(host, platformDomainHost)) {
      return next();
    }

    const match = host.match(params.subdomainRegex);
    let appName = match?.[1] || null;
    if (!appName) {
      const customDomainResolution = await resolveCustomDomain(host);
      if (customDomainResolution?.kind === "www_fallback_to_bare") {
        const url = new URL(c.req.url);
        return c.redirect(`https://${customDomainResolution.bareHostname}${url.pathname}${url.search}`, 301);
      }
      appName = customDomainResolution?.appName ?? null;
    }
    if (!appName) {
      return c.json(buildUnknownCustomDomainBody(host), 404);
    }

    try {
      const appRow = await params.findAppRow(appName);
      if (!appRow) {
        return c.json({ error: `App "${appName}" not found` }, 404);
      }

      const url = new URL(c.req.url);
      const pathname = url.pathname;
      if (isDeniedPublicAppSystemPath(pathname)) {
        return c.json({ error: "Not found" }, 404);
      }

      const analyticsRes = await maybeServeHostedAnalytics({
        pathname,
        appRow,
        host,
        c,
        functionsPath: params.functionsPath,
        rawSql: params.rawSql,
        analyticsRawSql: params.analyticsRawSql ?? params.rawSql,
        logger,
      });
      if (analyticsRes) {
        return analyticsRes;
      }

      const upgradeHeader = (c.req.header("upgrade") || "").toLowerCase();
      const remoteWsResponse = await maybeUpgradeRemoteServerAppWebSocket({
        c,
        host,
        appName,
        appRow,
        currentNodeId: params.currentNodeId,
        upgradeHeader,
        getLivePort: params.getLivePort,
        loadAppRouteNode: params.loadAppRouteNode,
        proxyRemoteNodeWebSocket: params.proxyRemoteNodeWebSocket,
        logger,
      });
      if (remoteWsResponse) {
        return remoteWsResponse;
      }

      const remoteNodeResponse = await maybeProxyRemoteServerAppRequest({
        c,
        host,
        appName,
        appRow,
        currentNodeId: params.currentNodeId,
        upgradeHeader,
        getLivePort: params.getLivePort,
        loadAppRouteNode: params.loadAppRouteNode,
        createProxyRequestReplayPlan: params.createProxyRequestReplayPlan,
        markLastAccess: params.markLastAccess,
        proxyRemoteNodeRequest: params.proxyRemoteNodeRequest,
        buildTransientProxyFailureBody,
        logger,
      });
      if (remoteNodeResponse) {
        return remoteNodeResponse;
      }

      let hasBackend = appRow.hasBackend;
      const hasStatic = !!appRow.staticDeployedAt;

      if (hasStatic && !hasBackend) {
        const liveStaticPort = params.getLivePort(appName) ?? (await params.resolveLiveRuntimePort(appName));
        if (!liveStaticPort) {
          return (
            recordNodeRouteMetric({ protocol: "http", kind: "local", appName }),
            await serveStaticFile({
              c,
              dataDir: appRow.dataDir,
              pathname,
              appName,
              recordBytes: (bytes) => params.addProxyMetric(appName, bytes),
              htmlHeadInjection: buildHostedAnalyticsScriptTag(appRow),
              htmlHeadInjectionSkipMarker: HOSTED_ANALYTICS_SCRIPT_PATH,
            })
          );
        }
        logger.warn(
          `[platform] ${appName}: hasStatic=true, hasBackend=false but live runtime found on :${liveStaticPort} — treating as fullstack`,
        );
        params.setAppPort(appName, liveStaticPort);
        hasBackend = true;
        void params.markHasBackend(appName).catch(() => {});
      }

      if (upgradeHeader === "websocket" && params.getBunServer()) {
        const appInternalToken = params.generateInternalToken(appRow.id, appRow.name);
        const ticketAuth = consumeGatewayRealtimeTicket({
          ticket: url.searchParams.get("ticket"),
          appName: appRow.name,
          secret: appInternalToken,
        });
        if (!ticketAuth.ok) {
          return new Response("Invalid realtime ticket", { status: ticketAuth.status });
        }
        const connectionLimit = await resolveRealtimeConnectionLimit({
          appName,
          rawSql: params.rawSql,
          logger,
        });
        const upgraded = params.getBunServer().upgrade(c.req.raw, {
          data: {
            gatewayAppName: appName,
            appName,
            appPort: params.getLivePort(appName) || appRow.port,
            pathname,
            connectionLimit,
            appInternalToken,
            authMode: ticketAuth.authMode,
            userId: ticketAuth.authMode === "authenticated" ? ticketAuth.userId : undefined,
            sessionId: ticketAuth.authMode === "authenticated" ? ticketAuth.sessionId : null,
            authExpiresAt: ticketAuth.authMode === "authenticated" ? ticketAuth.expiresAt : undefined,
          },
        });
        if (upgraded)
          return (
            recordNodeRouteMetric({ protocol: "ws", kind: "local", appName }),
            new Response(null, { status: 101 })
          );
        return c.json({ error: "WebSocket upgrade failed" }, 500);
      }

      try {
        params.markLastAccess(appName);
      } catch {
        /* ignore */
      }

      if (hasStatic && hasBackend) {
        const isApiRoute = isPublicAppBackendRoute(pathname);
        if (isApiRoute) {
          const storageRes = await maybeServePlatformStorage({
            pathname,
            appName,
            c,
            functionsPath: params.functionsPath,
            rawSql: params.rawSql,
          });
          if (storageRes) {
            recordNodeRouteMetric({ protocol: "http", kind: "local", appName });
            return storageRes;
          }
          recordNodeRouteMetric({ protocol: "http", kind: "local", appName });
          return await proxyToBackendWithRecovery({
            c,
            host,
            url,
            appRow,
            logger,
            createProxyRequestReplayPlan: params.createProxyRequestReplayPlan,
            buildAppRuntimeUnavailableResult: params.buildAppRuntimeUnavailableResult,
            ensureAppRuntimeReady: params.ensureAppRuntimeReady,
            getLivePort: params.getLivePort,
            setAppPort: params.setAppPort,
            removeAppPort: params.removeAppPort,
            removeAppPortIfEquals: params.removeAppPortIfEquals,
            wakeApp: params.wakeApp,
            provisionApp: params.provisionApp,
            setAppMeta: params.setAppMeta,
            markLastAccess: params.markLastAccess,
            hasLiveRuntimeProcess: params.hasLiveRuntimeProcess,
            resolveLiveRuntimePort: params.resolveLiveRuntimePort,
            reapRuntimeExits: params.reapRuntimeExits,
            markAppAsCrashed: params.markAppAsCrashed,
            addProxyMetric: params.addProxyMetric,
          });
        }
        return (
          recordNodeRouteMetric({ protocol: "http", kind: "local", appName }),
          await serveStaticFile({
            c,
            dataDir: appRow.dataDir,
            pathname,
            appName,
            recordBytes: (bytes) => params.addProxyMetric(appName, bytes),
            htmlHeadInjection: buildHostedAnalyticsScriptTag(appRow),
            htmlHeadInjectionSkipMarker: HOSTED_ANALYTICS_SCRIPT_PATH,
          })
        );
      }

      if (hasBackend) {
        const storageRes = await maybeServePlatformStorage({
          pathname,
          appName,
          c,
          functionsPath: params.functionsPath,
          rawSql: params.rawSql,
        });
        if (storageRes) {
          recordNodeRouteMetric({ protocol: "http", kind: "local", appName });
          return storageRes;
        }
        recordNodeRouteMetric({ protocol: "http", kind: "local", appName });
        return await proxyToBackendWithRecovery({
          c,
          host,
          url,
          appRow,
          logger,
          createProxyRequestReplayPlan: params.createProxyRequestReplayPlan,
          buildAppRuntimeUnavailableResult: params.buildAppRuntimeUnavailableResult,
          ensureAppRuntimeReady: params.ensureAppRuntimeReady,
          getLivePort: params.getLivePort,
          setAppPort: params.setAppPort,
          removeAppPort: params.removeAppPort,
          removeAppPortIfEquals: params.removeAppPortIfEquals,
          wakeApp: params.wakeApp,
          provisionApp: params.provisionApp,
          setAppMeta: params.setAppMeta,
          markLastAccess: params.markLastAccess,
          hasLiveRuntimeProcess: params.hasLiveRuntimeProcess,
          resolveLiveRuntimePort: params.resolveLiveRuntimePort,
          reapRuntimeExits: params.reapRuntimeExits,
          markAppAsCrashed: params.markAppAsCrashed,
          addProxyMetric: params.addProxyMetric,
        });
      }

      const liveBackendPort = params.getLivePort(appName) ?? (await params.resolveLiveRuntimePort(appName));
      if (liveBackendPort) {
        logger.warn(
          `[platform] ${appName}: hasBackend=false but live runtime found on :${liveBackendPort} — proxying anyway (flag mismatch)`,
        );
        params.setAppPort(appName, liveBackendPort);
        void params.markHasBackend(appName).catch(() => {});
        recordNodeRouteMetric({ protocol: "http", kind: "local", appName });
        return await proxyToBackendWithRecovery({
          c,
          host,
          url,
          appRow,
          logger,
          createProxyRequestReplayPlan: params.createProxyRequestReplayPlan,
          buildAppRuntimeUnavailableResult: params.buildAppRuntimeUnavailableResult,
          ensureAppRuntimeReady: params.ensureAppRuntimeReady,
          getLivePort: params.getLivePort,
          setAppPort: params.setAppPort,
          removeAppPort: params.removeAppPort,
          removeAppPortIfEquals: params.removeAppPortIfEquals,
          wakeApp: params.wakeApp,
          provisionApp: params.provisionApp,
          setAppMeta: params.setAppMeta,
          markLastAccess: params.markLastAccess,
          hasLiveRuntimeProcess: params.hasLiveRuntimeProcess,
          resolveLiveRuntimePort: params.resolveLiveRuntimePort,
          reapRuntimeExits: params.reapRuntimeExits,
          markAppAsCrashed: params.markAppAsCrashed,
          addProxyMetric: params.addProxyMetric,
        });
      }

      if (await params.hasLiveRuntimeProcess(appName)) {
        logger.warn(
          `[platform] ${appName}: hasBackend=false and runtime process exists without reconciled port`,
        );
        const unavailable = params.buildAppRuntimeUnavailableResult(appName, appRow.status);
        return c.json(buildRuntimeUnavailableBody(unavailable), unavailable.httpStatus);
      }

      return c.json({ error: `App "${appName}" is ${appRow.status}` }, 503);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      logger.error(`[platform] proxy error for ${appName}: ${message}`);
      return c.json({ error: "앱 서버에 연결할 수 없습니다", code: "APP_UNREACHABLE" }, 502);
    }
  });
}
